/* Optional room inspiration for two exact Linen SKUs. No form, price or purchase state changes. */
(() => {
  'use strict';
  if (window.KaliaAiRoomPreview) return;
  const handle = 'persilux-linen-fabric-roller-shades';
  const sourceProductId = '1151381949263522';
  const permitted = Object.freeze({ '1151381949263523': 'C156-1', '1151381949263527': 'C156-5' });
  const instances = new WeakMap();

  class KaliaAiRoomPreview {
    static safeUrl(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, document.baseURI || window.location.href);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
      } catch (_) { return ''; }
    }

    static manifest(studio) {
      const entries = new Map();
      const catalog = studio.querySelector('[data-ai-room-catalog][data-ai-assets-owned="true"]');
      if (!catalog || studio.dataset.sourceHandle !== handle || String(studio.config?.sourceProductId) !== sourceProductId) return entries;
      catalog.querySelectorAll('[data-ai-source-sku-id]').forEach(node => {
        const id = node.dataset.aiSourceSkuId, code = node.dataset.aiSkuCode;
        const src = this.safeUrl(node.dataset.aiRoomSrc);
        if (!Object.prototype.hasOwnProperty.call(permitted, id) || permitted[id] !== code || !src || entries.has(id)) return;
        entries.set(id, { id, code, src });
      });
      return entries;
    }

    static match(studio, entries) {
      if (studio.dataset.sourceHandle !== handle || String(studio.config?.sourceProductId) !== sourceProductId) return null;
      const source = studio.currentSourceSku?.();
      const id = String(source?.id || ''), entry = entries.get(id);
      if (!Object.prototype.hasOwnProperty.call(permitted, id) || !entry || permitted[id] !== source.sku || entry.code !== source.sku || source.options?.Style !== 'Light Filtering') return null;
      return { ...entry, color: String(source.options.Color || entry.code) };
    }

    constructor(studio, entries) {
      this.studio = studio; this.entries = entries; this.failed = new Set(); this.controller = new AbortController();
      const viewSwitch = studio.querySelector('.kps-view-switch');
      this.id = `KaliaAiRoom-${++KaliaAiRoomPreview.sequence}`;
      this.button = this.node('button', 'kair-tab', 'AI room preview');
      this.button.type = 'button'; this.button.id = `${this.id}-tab`; this.button.dataset.viewButton = 'ai-room';
      this.button.setAttribute('role', 'tab'); this.button.setAttribute('aria-selected', 'false');
      this.button.setAttribute('aria-controls', this.id); this.button.tabIndex = -1; this.button.hidden = true;
      this.panel = this.node('div', 'kair-panel'); this.panel.id = this.id; this.panel.dataset.view = 'ai-room'; this.panel.hidden = true;
      this.panel.setAttribute('role', 'tabpanel'); this.panel.setAttribute('aria-labelledby', this.button.id);
      this.media = this.node('div', 'kair-media');
      this.status = this.node('p', 'kair-status'); this.status.setAttribute('role', 'status'); this.status.setAttribute('aria-live', 'polite');
      this.caption = this.node('p', 'kair-caption');
      const note = this.node('p', 'kair-note', 'AI-generated room inspiration. Use the real fabric swatch to confirm texture, color and opacity. Room details and shade proportions are illustrative.');
      this.panel.append(this.media, this.status, this.caption, note);
      viewSwitch.append(this.button); viewSwitch.parentNode.insertBefore(this.panel, studio.querySelector('[data-view="illustration"]'));
      const options = { signal: this.controller.signal };
      this.button.addEventListener('click', () => { this.render(); if (this.button.hidden) return; studio.showView('ai-room'); this.load(); }, options);
      // Original tabs retain their click handlers; this adds correct three-tab arrow navigation only while AI is available.
      viewSwitch.addEventListener('keydown', event => {
        if (this.button.hidden || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const target = event.target.closest('[data-view-button]');
        const tabs = [...viewSwitch.querySelectorAll('[data-view-button]')].filter(tab => !tab.hidden);
        const index = tabs.indexOf(target); if (index < 0) return;
        event.preventDefault(); event.stopImmediatePropagation();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
        studio.showView(tabs[next].dataset.viewButton); if (tabs[next] === this.button) this.load(); tabs[next].focus();
      }, { ...options, capture: true });
      studio.addEventListener('kalia:product-configuration-change', () => this.render(), options);
      this.render();
    }

    node(tag, className, text) {
      const node = document.createElement(tag); if (className) node.className = className; if (text !== undefined) node.textContent = text; return node;
    }

    active() { return this.button.getAttribute('aria-selected') === 'true'; }

    render() {
      const next = this.constructor.match(this.studio, this.entries);
      const available = next && !this.failed.has(next.id);
      if (!available) {
        const focused = document.activeElement === this.button;
        if (this.active()) this.studio.showView('photo');
        this.button.hidden = true; this.panel.hidden = true; this.button.tabIndex = -1; this.button.setAttribute('aria-selected', 'false');
        this.entry = null; this.image = null; this.media.replaceChildren(); this.caption.textContent = ''; this.status.textContent = '';
        delete this.panel.dataset.aiSourceSkuId;
        if (focused) this.studio.querySelector('[data-view-button="photo"]')?.focus();
        return;
      }
      const changed = next.id !== this.entry?.id || next.src !== this.entry?.src;
      this.entry = next; this.button.hidden = false; this.panel.dataset.aiSourceSkuId = next.id;
      this.caption.textContent = `AI color inspiration · ${next.color} · Light Filtering`;
      if (changed) { this.image = null; this.media.replaceChildren(); this.status.textContent = ''; }
      if (this.active()) this.load();
    }

    load() {
      const entry = this.entry;
      if (!entry || this.image) return;
      const image = this.node('img', 'kair-image');
      image.alt = `AI-generated room inspiration for ${entry.color}, Light Filtering linen roller shade. Visual inspiration only.`;
      image.dataset.aiSourceSkuId = entry.id; image.decoding = 'async'; image.hidden = true;
      image.addEventListener('load', () => {
        if (this.image !== image || this.entry?.id !== entry.id) return;
        image.hidden = false; this.status.textContent = '';
      });
      image.addEventListener('error', () => {
        if (this.image !== image || this.entry?.id !== entry.id) return;
        // Optional inspiration must never leave a stale color on screen or block the real product photos.
        this.failed.add(entry.id); this.render();
      });
      this.status.textContent = 'Loading AI room preview…'; this.image = image; this.media.replaceChildren(image); image.src = entry.src;
    }

    destroy() { this.controller.abort(); if (this.active()) this.studio.showView('photo'); this.button.remove(); this.panel.remove(); }
  }
  KaliaAiRoomPreview.sequence = 0;
  window.KaliaAiRoomPreview = KaliaAiRoomPreview;
  const attach = studio => {
    if (!studio?.ready || instances.has(studio)) return;
    const entries = KaliaAiRoomPreview.manifest(studio);
    if (!entries.size || !studio.querySelector('.kps-view-switch') || !studio.querySelector('[data-view="illustration"]')) return;
    instances.set(studio, new KaliaAiRoomPreview(studio, entries));
  };
  const start = () => document.querySelectorAll('kalia-product-studio').forEach(attach);
  document.addEventListener('kalia:product-studio-ready', event => attach(event.target));
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('kalia-product-studio').forEach(studio => { instances.get(studio)?.destroy(); instances.delete(studio); }));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
